import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";

export default function ItineraryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [itin, setItin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/itineraries/${slug}`).then((r) => {
      setItin(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const isFree = itin && itin.price_usd === 0;
  const purchased = itin?.purchased;
  const isOwner = itin?.is_owner;

  const handleBuy = async () => {
    if (!user) {
      toast.info("Please log in or sign up to continue.");
      navigate(`/auth?mode=signin&next=/itinerary/${slug}`);
      return;
    }
    if (purchased) {
      navigate(`/library/${slug}`);
      return;
    }
    setBuying(true);
    try {
      // Free path — unchanged flow
      if (isFree) {
        await api.post(`/itineraries/${itin.itinerary_id}/unlock-free`);
        toast.success("Added to your library!");
        await refresh();
        navigate(`/library/${slug}`);
        return;
      }
      // Paid path — Razorpay checkout
      const { data: order } = await api.post("/payments/create-order", {
        itinerary_id: itin.itinerary_id,
      });
      const resp = await openRazorpayCheckout(order, itin.title);
      await api.post("/payments/verify", {
        razorpay_order_id:   resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature:  resp.razorpay_signature,
        itinerary_id:        itin.itinerary_id,
      });
      toast.success("Payment successful! Itinerary added to your library.");
      await refresh();
      navigate(`/library/${slug}`);
    } catch (err) {
      if (err?.message === "Payment cancelled") {
        toast.info("Payment cancelled.");
      } else {
        toast.error(err.response?.data?.detail || err.message || "Could not complete purchase");
      }
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--paper)]">
        <Navbar />
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 py-20 text-center mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink3)]">◆ Loading…</div>
      </div>
    );
  }
  if (!itin) {
    return (
      <div className="min-h-screen bg-[var(--paper)]">
        <Navbar />
        <div className="max-w-[800px] mx-auto px-6 py-24 text-center">
          <span className="eyebrow">◆ Not found</span>
          <h1 className="h-display text-[40px] mt-3">This route doesn't exist.</h1>
          <Link to="/explore" className="btn btn-solid mt-6 inline-flex">Browse marketplace →</Link>
        </div>
      </div>
    );
  }

  const day1 = itin.days?.[0];
  const lockedCount = Math.max(0, itin.duration_days - 1);

  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="itinerary-detail">
      <Navbar />

      {/* Back */}
      <div className="max-w-[1280px] mx-auto px-6 sm:px-10 pt-8">
        <Link to="/explore" data-testid="back-to-marketplace" className="mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink2)] hover:text-[var(--red)] transition-colors">
          ← Back to marketplace
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 mt-6 mb-10">
        <div
          className="relative rounded-[24px] overflow-hidden border-[1.5px] border-[var(--line)] aspect-[16/7] bg-cover bg-center"
          style={{ backgroundImage: `url(${itin.cover_url})` }}
          data-testid="hero-cover"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.25) 100%)",
            }}
          />
          <div
            className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 max-w-[800px]"
            style={{ color: "#FBF7F2" }}
          >
            <span
              className="mono text-[10px] tracking-[0.2em] uppercase"
              style={{ color: "#FBF7F2", opacity: 0.9 }}
            >
              ◆ {itin.destination_country} · {itin.destination_city} · {itin.duration_days} days
            </span>
            <h1
              className="h-display text-[36px] sm:text-[56px] mt-3 leading-[0.95]"
              style={{ color: "#FBF7F2" }}
            >
              {itin.title}
            </h1>
          </div>
          {itin.badge && (
            <div className="absolute top-6 left-6 bg-white/95 border-[1.5px] border-[var(--red)] text-[var(--red)] mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-md font-bold">
              {itin.badge}
            </div>
          )}
        </div>
      </section>

      <div className="max-w-[1280px] mx-auto px-6 sm:px-10 grid lg:grid-cols-[1fr_380px] gap-10 pb-20">
        {/* MAIN COLUMN */}
        <div>
          {/* Meta row */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="meta-row">
            {itin.trip_type?.map((t) => <span key={t} className="tag">{t}</span>)}
            <span className="tag" style={{ color: "var(--red)", borderColor: "var(--red)" }}>{itin.budget_range}</span>
            {itin.best_season?.map((s) => <span key={s} className="tag">{s}</span>)}
            {itin.estimated_cost_usd && (
              <span className="tag" style={{ color: "var(--ink2)", fontWeight: 600 }}>
                ≈ {itin.estimated_cost_usd}
              </span>
            )}
          </div>

          <p className="text-[16px] leading-[1.7] text-[var(--ink2)] mb-8 max-w-[680px]">
            {itin.description}
          </p>

          {/* Creator card */}
          <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-5 flex items-center gap-4 mb-10" data-testid="creator-card">
            <div
              className="av-sm"
              style={{ width: 56, height: 56, fontSize: 16, background: itin.seller?.accent_color || "var(--ink)" }}
            >
              {itin.seller?.initials}
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-bold">{itin.seller?.name}</div>
              <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)] mt-1">
                Verified creator · From {itin.destination_city}
              </div>
            </div>
            <Link
              to={`/creator/${itin.seller?.user_id}`}
              data-testid="view-profile"
              className="btn btn-ghost btn-sm"
            >
              View profile →
            </Link>
          </div>

          {/* What's inside */}
          <h3 className="text-[22px] font-extrabold tracking-[-0.02em] mb-5">What's inside</h3>

          {/* Day 1 — fully visible */}
          {day1 && <DayBlock day={day1} unlocked />}

          {/* Locked days */}
          {lockedCount > 0 && !purchased && !isFree && !isOwner && (
            <div className="relative mt-6" data-testid="locked-section">
              <div className="space-y-4 select-none pointer-events-none" aria-hidden>
                {[...Array(Math.min(lockedCount, 3)).keys()].map((i) => (
                  <div key={i} className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-6">
                    <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink3)] mb-2">
                      Day {i + 2}
                    </div>
                    <div className="h-7 w-3/4 bg-[var(--line)] rounded mb-3" />
                    <div className="h-3 w-full bg-[var(--line)]/60 rounded mb-2" />
                    <div className="h-3 w-5/6 bg-[var(--line)]/60 rounded mb-2" />
                    <div className="h-3 w-2/3 bg-[var(--line)]/60 rounded" />
                  </div>
                ))}
              </div>
              <div
                className="absolute inset-0 backdrop-blur-md bg-[var(--paper)]/70 rounded-[18px] flex flex-col items-center justify-center text-center px-6"
                data-testid="locked-overlay"
              >
                <span className="eyebrow">◆ {lockedCount} more days locked</span>
                <h3 className="h-display text-[28px] mt-3 max-w-[420px] leading-tight">
                  Unlock all {itin.duration_days} days from {itin.seller?.name}.
                </h3>
                <button
                  onClick={handleBuy}
                  data-testid="locked-cta"
                  className="btn btn-solid btn-lg mt-6"
                >
                  {isFree ? "Get for free →" : `Unlock — ₹${Number(itin.price_usd)} →`}
                </button>
              </div>
            </div>
          )}

          {/* Free, purchased, or owner: show all days */}
          {(purchased || isFree || isOwner) && itin.days?.slice(1).map((d) => (
            <DayBlock key={d.day_id} day={d} unlocked />
          ))}
        </div>

        {/* RIGHT — buy box (or owner edit box) */}
        <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="buy-box">
          {isOwner ? (
            <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-7">
              <span className="eyebrow">◆ Your itinerary</span>
              <div className="serif text-[32px] mt-2 leading-none">
                {itin.status === "live" ? "Live on marketplace" : "Draft"}
              </div>
              <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)] mt-2">
                {itin.total_sales || 0} sales · ₹{Number(itin.price_usd) || 0} listed
              </div>
              <Link
                to={`/seller/edit/${itin.itinerary_id}`}
                data-testid="owner-edit"
                className="btn btn-solid btn-lg full mt-6"
              >
                Edit itinerary →
              </Link>
              <Link
                to="/seller"
                className="btn btn-ghost btn-sm full mt-2"
              >
                ← Back to dashboard
              </Link>
              <p className="mt-6 text-[12px] leading-[1.6] text-[var(--ink3)] italic">
                You're the creator — all days are unlocked for you.
              </p>
            </div>
          ) : (
            <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-7">
            <span className="eyebrow">◆ Pay once · yours forever</span>
            <div className="serif text-[44px] text-[var(--red)] mt-2 leading-none" data-testid="price-display">
              {isFree ? "Free" : `₹${Number(itin.price_usd)}`}
            </div>
            <div className="mono text-[10px] tracking-[0.15em] uppercase text-[var(--ink3)] mt-2">
              {isFree ? "No card required" : "INR · one-time"}
            </div>

            {!purchased && (
              <div
                className="mt-6 bg-[#FCEAEC] border-[1.5px] border-[var(--red)]/30 text-[var(--red2)] rounded-[12px] p-4 text-[12px] leading-[1.55]"
                data-testid="no-refund"
              >
                <strong>⚠ No refunds.</strong> The Day 1 preview above shows you exactly what you are getting.
              </div>
            )}

            <button
              onClick={handleBuy}
              disabled={buying}
              data-testid="buy-button"
              className="btn btn-solid btn-lg full mt-6"
            >
              {buying ? "…" : purchased ? "Open in library →" : isFree ? "Get for free →" : `Add to library →`}
            </button>

            <ul className="mt-6 space-y-2 text-[13px] text-[var(--ink2)]">
              <PerkLine>Lifetime access — no subscription</PerkLine>
              <PerkLine>Updated when {itin.seller?.name?.split(" ")[0]} changes things</PerkLine>
              <PerkLine>Mobile reader + offline-friendly cards</PerkLine>
              <PerkLine>Day 1 always free, no login required</PerkLine>
            </ul>
          </div>
          )}
        </aside>
      </div>

      <Footer />
    </div>
  );
}

const PerkLine = ({ children }) => (
  <li className="flex gap-2 items-start">
    <span className="text-[var(--red)] font-bold leading-[1.45]">✓</span>
    <span>{children}</span>
  </li>
);

function DayBlock({ day, unlocked }) {
  return (
    <div
      className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-6 mb-4"
      data-testid={`day-${day.day_number}`}
    >
      <div className="flex items-baseline gap-3 mb-4">
        <span className="serif text-[36px] text-[var(--red)] leading-none">{String(day.day_number).padStart(2, "0")}</span>
        <div>
          <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink3)]">Day {day.day_number}</div>
          <h4 className="text-[20px] font-extrabold tracking-[-0.02em] leading-tight">{day.day_title}</h4>
        </div>
      </div>
      {unlocked && day.blocks?.length > 0 && (
        <div className="space-y-5 mt-5 border-t border-dashed border-[var(--line)] pt-5">
          {day.blocks.map((b) => <Block key={b.block_id} block={b} />)}
        </div>
      )}
    </div>
  );
}

function Block({ block }) {
  return (
    <div className="grid sm:grid-cols-[100px_1fr] gap-3 sm:gap-5">
      <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--red)] pt-1">
        {block.time_of_day}
      </div>
      <div>
        <div className="text-[16px] font-bold leading-tight mb-1">{block.activity_name}</div>
        <p className="text-[14px] leading-[1.6] text-[var(--ink2)]">{block.description}</p>
        {block.creator_tip && (
          <div className="mt-3 border-l-[3px] border-[var(--red)] pl-3 italic text-[13px] text-[var(--ink)] leading-[1.55] bg-[#FCEAEC]/40 py-2 pr-3 rounded-r">
            <span className="not-italic mono text-[9px] tracking-[0.2em] uppercase text-[var(--red)] block mb-1">◆ Creator tip</span>
            {block.creator_tip}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          {block.location_name && <span className="tag">📍 {block.location_name}</span>}
          {block.approx_cost && <span className="tag" style={{ color: "var(--red)", borderColor: "var(--red)/40" }}>{block.approx_cost}</span>}
        </div>
      </div>
    </div>
  );
}
